import { jwtDecode } from "jwt-decode";
import CryptoJS from "crypto-js";
import axiosInstance from "../../axios";

const STORAGE_KEY = process.env.REACT_APP_STORAGE_KEY || "navya_storage_key";

const encryptToken = (token: string) => {
  try {
    return CryptoJS.AES.encrypt(token, STORAGE_KEY).toString();
  } catch (e) {
    return token;
  }
};

const decryptToken = (cipherText: string) => {
  try {
    const bytes = CryptoJS.AES.decrypt(cipherText, STORAGE_KEY);
    const decrypted = bytes.toString(CryptoJS.enc.Utf8);
    return decrypted || cipherText;
  } catch (e) {
    return cipherText;
  }
};

const isProbablyJWT = (t: any) =>
  typeof t === "string" && t.split(".").length === 3;

export const setSession = (accessToken: any) => {
  if (accessToken !== "") {
    // ensure we have the raw token string
    let rawToken = accessToken;
    if (!isProbablyJWT(rawToken)) {
      // try to decrypt if caller accidentally passed encrypted value
      const maybe = decryptToken(rawToken);
      if (isProbablyJWT(maybe)) rawToken = maybe;
    }

    const encrypted = encryptToken(String(rawToken));
    localStorage.setItem("accessToken", encrypted);
    axiosInstance.defaults.headers.common.Authorization = `Bearer ${rawToken}`;
  } else {
    localStorage.removeItem("accessToken");
    delete axiosInstance.defaults.headers.common.Authorization;
  }
};

const isValidToken = (accessToken: any) => {
  const currentTime = Date.now() / 1000;
  return accessToken.exp > currentTime;
};

export const getDecryptedToken = () => {
  const stored = localStorage.getItem("accessToken");
  if (!stored) return null;
  if (isProbablyJWT(stored)) return stored;
  return decryptToken(stored);
};

export const decodeToken = (accessToken: any) => {
  // If caller passed nothing, try reading from storage
  let tokenToDecode = accessToken ?? localStorage.getItem("accessToken");

  if (!tokenToDecode) return null;

  // If the value is encrypted, decrypt it
  if (!isProbablyJWT(tokenToDecode)) {
    tokenToDecode = decryptToken(tokenToDecode);
  }

  try {
    const decodedToken = jwtDecode(tokenToDecode);
    const isValid = isValidToken(decodedToken);
    if (isValid) {
      return true;
    }
  } catch (e) {
    // fallthrough to cleanup
  }

  localStorage.removeItem("accessToken");
  delete axiosInstance.defaults.headers.common.Authorization;
  return null;
};
